import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, BadgeCheck } from "lucide-react";
import { OFFERS, BOOKING_URL } from "@/data";
import { Reveal, SectionHead } from "@/components/Reveal";
import { track } from "@/hooks/useAnalytics";

export const OfferCard = ({ offer, index }) => {
  const free = offer.kicker.toLowerCase().includes("complimentary");
  return (
    <Reveal delay={0.08 * index} y={24}>
      <article
        data-testid={`offer-card-${offer.id}`}
        className="flex h-full flex-col rounded-2xl border border-ink/8 bg-white p-8 shadow-[0_2px_20px_-8px_rgba(18,26,21,0.1)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-16px_rgba(18,26,21,0.22)]"
      >
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-leafdeep">{offer.kicker}</p>
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest text-leaf">
            {free ? <Gift className="h-4 w-4" /> : <BadgeCheck className="h-4 w-4" />}
          </span>
        </div>
        <h3 className="mt-4 font-serif text-2xl leading-snug text-ink">{offer.title}</h3>
        <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-fern">{offer.text}</p>
        <p className="mt-5 border-t border-ink/10 pt-4 font-sans text-xs leading-relaxed text-fern/80">{offer.fine}</p>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`offer-book-${offer.id}`}
            onClick={() => track("book_click", { location: `offer-${offer.id}` })}
            className="group inline-flex items-center gap-2 rounded-md bg-forest px-5 py-2.5 font-sans text-sm font-medium text-cream transition-[background-color,box-shadow] duration-300 hover:bg-pine hover:shadow-[0_10px_30px_-8px_rgba(36,54,47,0.5)]"
          >
            {offer.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <Link to={offer.link} data-testid={`offer-learn-${offer.id}`} className="font-sans text-sm font-medium text-ink transition-colors hover:text-leafdeep">
            Learn more
          </Link>
        </div>
      </article>
    </Reveal>
  );
};

const Offers = () => (
  <section id="offers" data-testid="offers-section" className="bg-sage py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHead
          dark
          overline="Special offers"
          title={
            <>
              A few ways to make the first step{" "}
              <span className="italic text-fern">a bit easier.</span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <div className="max-w-sm">
            <p className="font-sans text-sm leading-relaxed text-fern">
              Everyone deserves straight answers before they spend money on their teeth. These are for new patients — just mention the offer when you book.
            </p>
            <Link to="/offers" data-testid="offers-view-all-link" className="group mt-4 inline-flex items-center gap-2 font-sans text-sm font-medium text-ink transition-colors hover:text-leafdeep">
              All offers & the fine print <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {OFFERS.map((o, i) => <OfferCard key={o.id} offer={o} index={i} />)}
      </div>
    </div>
  </section>
);

export default Offers;
